package org.vreo.devicehub.features.core.data

import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.network.sockets.SocketTimeoutException
import io.ktor.client.statement.HttpResponse
import io.ktor.util.network.UnresolvedAddressException
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.ensureActive

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import kotlin.time.Duration.Companion.milliseconds

/**
 * Safely executes an HTTP call and handles common network-related exceptions,
 * converting the [HttpResponse] into an Empty [Result] (Result<Unit, E>).
 *
 * This version is for API calls that return an empty body or no content on success.
 */
suspend fun safeCallEmpty(
    execute: suspend () -> HttpResponse,
    maxRetries: Int = 2,
    retryDelay: Long = 100,
): Result<Unit, DataError.Remote> {
    var attempts = 0

    while (attempts < maxRetries) {
        val response = try {
            execute()
        } catch (_: SocketTimeoutException) {
            attempts++
            if (attempts < maxRetries) {
                delay(retryDelay.milliseconds)
                continue // Try again
            }
            return Result.Error(DataError.Remote.REQUEST_TIMEOUT)

        } catch (_: UnresolvedAddressException) {
            return Result.Error(DataError.Remote.NO_INTERNET)
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            return Result.Error(DataError.Remote.UNKNOWN)
        }

        return responseToEmptyResult(response)
    }

    return Result.Error(DataError.Remote.REQUEST_TIMEOUT)
}

/**
 * Converts an [HttpResponse] into an Empty [Result] (Result<Unit, E>).
 *
 * This function handles the HTTP status codes. If the response is successful (2xx status code),
 * it returns [Result.Success] with [Unit] without attempting to parse the body.
 */
fun responseToEmptyResult(
    response: HttpResponse
): Result<Unit, DataError.Remote> {
    return when (response.status.value) {
        // --- KEY CHANGE: Return Success(Unit) immediately for 2xx codes ---
        in 200..299 -> Result.Success(Unit)

        // Error handling remains the same
        HttpStatusCode.NotAcceptable.value -> Result.Error(DataError.Remote.INVALID_CREDENTIALS)
        HttpStatusCode.BadRequest.value -> Result.Error(DataError.Remote.INVALID_ARGUMENT)
        HttpStatusCode.NotFound.value -> Result.Error(DataError.Remote.NOT_FOUND)
        HttpStatusCode.Conflict.value -> Result.Error(DataError.Remote.USER_ALREADY_EXISTS)
        HttpStatusCode.InsufficientStorage.value -> Result.Error(DataError.Remote.STORAGE_FULL)
        HttpStatusCode.ServiceUnavailable.value -> Result.Error(DataError.Remote.STORAGE_NOT_REACHABLE)
        HttpStatusCode.TooManyRequests.value -> Result.Error(DataError.Remote.RATE_LIMIT_EXCEEDED)
        HttpStatusCode.ServiceUnavailable.value -> Result.Error(DataError.Remote.SERVICE_UNAVAILABLE)
        HttpStatusCode.Locked.value -> Result.Error(DataError.Remote.USER_LOCKED)
        else -> Result.Error(DataError.Remote.UNKNOWN)
    }
}

/**
 * Safely executes an HTTP call and handles common network-related exceptions,
 * converting the [HttpResponse] into a [Result] type.
 *
 * This function is designed to wrap network operations, providing a standardized
 * way to catch and categorize network errors such as timeouts, no internet
 * connection, and other unexpected exceptions, returning a [Result.Error] with
 * a [DataError.Remote] type.
 *
 * If the execution is successful, it delegates to [responseToResult] to parse
 * the [HttpResponse] body.
 *
 * @param T The type of the expected response body.
 * @param execute The suspendable function that executes the network call.
 * @param maxRetries The maximum number of times to retry the request if it fails.
 * @param retryDelay The initial delay in milliseconds before the first retry.
 * @return A [Result] object. On success, it contains the deserialized body of type [T].
 * On failure, it contains a [DataError.Remote] indicating the reason for failure,
 */
suspend inline fun <reified T> safeCall(
    noinline execute: suspend () -> HttpResponse,
    maxRetries: Int = 3,
    retryDelay: Long = 500,
): Result<T, DataError.Remote> {
    var attempts = 0

    while (attempts < maxRetries) {
        val response = try {
            execute()
        } catch (_: SocketTimeoutException) {
            attempts++
            if (attempts < maxRetries) {
                delay(retryDelay.milliseconds)
                continue // Try again
            }
            return Result.Error(DataError.Remote.REQUEST_TIMEOUT)

        } catch (_: UnresolvedAddressException) {
            return Result.Error(DataError.Remote.NO_INTERNET)
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            return Result.Error(DataError.Remote.UNKNOWN)
        }

        return responseToResult(response)
    }

    // If the loop finishes without returning, it means all retries failed due to timeout.
    return Result.Error(DataError.Remote.REQUEST_TIMEOUT)
}

/**
 * Converts an [HttpResponse] into a [Result] object, attempting to deserialize
 * the response body into the specified type [T].
 *
 * This function handles the HTTP status codes and the deserialization process.
 * If the response is successful (2xx status code), it attempts to parse the body.
 * If the body cannot be transformed to the expected type, it returns a [DataError.Remote.SERIALIZATION] error.
 * For non-successful HTTP status codes, it maps them to appropriate [DataError.Remote] types.
 *
 * @param T The type to which the response body should be deserialized.
 * @param response The [HttpResponse] to be processed.
 * @return A [Result] object. On success, it contains the deserialized body of type [T].
 * On failure, it contains a [DataError.Remote] indicating the reason for failure,
 * such as network error, server error, serialization error, or unknown error.
 */
suspend inline fun <reified T> responseToResult(
    response: HttpResponse
): Result<T, DataError.Remote> {
    return when (response.status.value) {
        in 200..299 -> {
            try {
                Result.Success(response.body<T>())
            } catch (_: NoTransformationFoundException) {
                Result.Error(DataError.Remote.SERIALIZATION)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                Result.Error(DataError.Remote.UNKNOWN)
            }
        }

        HttpStatusCode.NotAcceptable.value -> Result.Error(DataError.Remote.INVALID_CREDENTIALS)
        HttpStatusCode.BadRequest.value -> Result.Error(DataError.Remote.INVALID_ARGUMENT)
        HttpStatusCode.NotFound.value -> Result.Error(DataError.Remote.NOT_FOUND)
        HttpStatusCode.Conflict.value -> Result.Error(DataError.Remote.USER_ALREADY_EXISTS)
        HttpStatusCode.InsufficientStorage.value -> Result.Error(DataError.Remote.STORAGE_FULL)
        HttpStatusCode.ServiceUnavailable.value -> Result.Error(DataError.Remote.STORAGE_NOT_REACHABLE)
        HttpStatusCode.TooManyRequests.value -> Result.Error(DataError.Remote.RATE_LIMIT_EXCEEDED)
        HttpStatusCode.ServiceUnavailable.value -> Result.Error(DataError.Remote.SERVICE_UNAVAILABLE)
        HttpStatusCode.Locked.value -> Result.Error(DataError.Remote.USER_LOCKED)
        else -> Result.Error(DataError.Remote.UNKNOWN)
    }
}